import { useState, useMemo } from 'react';
import {
  Truck,
  Wallet,
  Users,
  Wrench,
  Sparkle,
  CaretRight,
} from '@phosphor-icons/react';
import { MilesChatPreset } from './milesTypes';
import type { MilesPresetCategory } from '../../types/apiTypes';

interface MilesPresetPromptsProps {
  prompts: MilesChatPreset[];
  onSelectPrompt: (promptText: string) => void;
}

type CategoryFilter = 'all' | MilesPresetCategory;

interface CategoryConfig {
  label: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
}

const CATEGORY_CONFIG: Record<CategoryFilter, CategoryConfig> = {
  all: {
    label: 'All',
    icon: Sparkle,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    borderColor: 'border-primary',
  },
  financial: {
    label: 'Financial',
    icon: Wallet,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-300',
  },
  fleet: {
    label: 'Fleet',
    icon: Truck,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
  },
  drivers: {
    label: 'Drivers',
    icon: Users,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-300',
  },
  maintenance: {
    label: 'Maintenance',
    icon: Wrench,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
  },
  general: {
    label: 'General',
    icon: Sparkle,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-300',
  },
};

const CATEGORY_ORDER: CategoryFilter[] = [
  'all',
  'fleet',
  'financial',
  'drivers',
  'maintenance',
  'general',
];

/**
 * MilesPresetPrompts - Category-filtered prompt selector
 *
 * Robinhood-inspired design with:
 * - Horizontal scrollable category chips
 * - Filtered list of prompts with smooth transitions
 * - Category-colored badges on each prompt
 * - Touch-friendly tap targets
 */
function MilesPresetPrompts({
  prompts,
  onSelectPrompt,
}: MilesPresetPromptsProps) {
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryFilter>('all');

  // Get unique categories that have prompts
  const availableCategories = useMemo(() => {
    const categoriesWithPrompts = new Set(prompts.map((p) => p.category));
    return CATEGORY_ORDER.filter(
      (cat) =>
        cat === 'all' || categoriesWithPrompts.has(cat as MilesPresetCategory)
    );
  }, [prompts]);

  // Filter prompts by selected category
  const filteredPrompts = useMemo(() => {
    if (selectedCategory === 'all') {
      return prompts;
    }
    return prompts.filter((p) => p.category === selectedCategory);
  }, [prompts, selectedCategory]);

  if (prompts.length === 0) {
    return null;
  }

  return (
    <div className="w-full overflow-hidden text-left">
      {/* Category Filter Chips */}
      <div className="mb-3">
        <p className="mb-2 text-[14px] font-medium text-gray-500">
          Quick questions:
        </p>
        <div className="flex flex-wrap gap-1.5">
          {availableCategories.map((category) => {
            const config = CATEGORY_CONFIG[category];
            const Icon = config.icon;
            const isSelected = selectedCategory === category;

            return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-[13px] font-medium transition-all duration-200 ${
                  isSelected
                    ? `${config.bgColor} ${config.borderColor} ${config.color}`
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Icon size={14} weight={isSelected ? 'fill' : 'regular'} />
                <span>{config.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Prompt List */}
      <div className="space-y-1.5">
        {filteredPrompts.map((preset) => {
          const categoryConfig = CATEGORY_CONFIG[preset.category];
          const CategoryIcon = categoryConfig.icon;

          return (
            <button
              key={preset.preset_id}
              onClick={() => onSelectPrompt(preset.prompt_text)}
              className="group flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left transition-all duration-200 hover:border-primary/30 hover:bg-primary/[0.02] hover:shadow-sm active:scale-[0.99]"
            >
              {/* Category Icon Badge */}
              <div
                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded ${categoryConfig.bgColor}`}
              >
                <CategoryIcon
                  size={14}
                  weight="fill"
                  className={categoryConfig.color}
                />
              </div>

              {/* Prompt Text */}
              <span className="min-w-0 flex-1 truncate text-[15px] text-gray-700 group-hover:text-gray-900">
                {preset.display_text}
              </span>

              {/* Arrow indicator */}
              <CaretRight
                size={14}
                className="flex-shrink-0 text-gray-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary"
              />
            </button>
          );
        })}
      </div>

      {/* Prompt count indicator */}
      <p className="mt-3 text-left text-[13px] text-gray-400">
        {selectedCategory !== 'all' && (
          <button
            onClick={() => setSelectedCategory('all')}
            className="ml-1 text-primary hover:underline"
          >
            · Show all
          </button>
        )}
      </p>
    </div>
  );
}

export default MilesPresetPrompts;
